import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Tooltip, Grid, Dropdown } from 'antd'
import {
  DatabaseOutlined,
  DollarOutlined,
  TagOutlined,
  TeamOutlined,
  GiftOutlined,
  CloudUploadOutlined,
  LineChartOutlined,
  EllipsisOutlined
} from '@ant-design/icons'
import { featureTierService } from '../services/featureTierService'
import type { FeatureTier } from '../types/featureTier.types'
import type { User } from '../types/milestone.types'

/**
 * WorkflowNav - Module navigation
 *
 * 7 primary modules: Data, Cost, Price, Negotiate, Incentivize, Publish, Insights
 * Uses breakpoint-based visibility with "More" dropdown for overflow.
 * Feature tier gating hides Dev-only modules from GA users.
 *
 * UX principles:
 * - No horizontal scrolling (use "More" dropdown instead)
 * - Unified "borderless at rest" button style (Linear/Notion pattern)
 * - Keep module order (overflow from the right)
 * - Left-to-right order implies sequence
 */

const { useBreakpoint } = Grid

interface WorkflowModule {
  key: string
  label: string
  description: string
  path: string
  icon: React.ComponentType<{ className?: string }>
  tier: FeatureTier
}

const WORKFLOW_MODULES: WorkflowModule[] = [
  {
    key: 'data',
    label: 'Data',
    description: 'Import your catalog and product data',
    path: '/data',
    icon: DatabaseOutlined,
    tier: 'GA'
  },
  {
    key: 'cost',
    label: 'Cost',
    description: 'Upload and manage vendor cost files',
    path: '/costs',
    icon: DollarOutlined,
    tier: 'GA'
  },
  {
    key: 'price',
    label: 'Price',
    description: 'Review and approve calculated prices',
    path: '/prices',
    icon: TagOutlined,
    tier: 'GA'
  },
  {
    key: 'negotiate',
    label: 'Negotiate',
    description: 'Manage quotes and customer negotiations',
    path: '/negotiate',
    icon: TeamOutlined,
    tier: 'Dev'
  },
  {
    key: 'incent',
    label: 'Incentivize',
    description: 'Configure incentive programs and payouts',
    path: '/incent',
    icon: GiftOutlined,
    tier: 'Dev'
  },
  {
    key: 'publish',
    label: 'Publish',
    description: 'Export to QuickBooks, Square, Shopify',
    path: '/exports',
    icon: CloudUploadOutlined,
    tier: 'GA'
  },
  {
    key: 'insight',
    label: 'Insights',
    description: 'Analytics, reports, and dashboards',
    path: '/insight',
    icon: LineChartOutlined,
    tier: 'Dev'
  }
]

interface WorkflowNavProps {
  user: User
}

interface ModuleButtonProps {
  module: WorkflowModule
  isActive: boolean
  showLabel: boolean
  onClick: () => void
}

function ModuleButton({ module, isActive, showLabel, onClick }: ModuleButtonProps) {
  const IconComponent = module.icon

  return (
    <Tooltip
      title={
        <div>
          <strong>{module.label}</strong>
          <br />
          <span style={{ opacity: 0.8 }}>{module.description}</span>
        </div>
      }
      placement="bottom"
    >
      <button
        className={`header-btn${isActive ? ' active' : ''}`}
        onClick={onClick}
        aria-current={isActive ? 'page' : undefined}
      >
        <IconComponent />
        {showLabel && <span>{module.label}</span>}
      </button>
    </Tooltip>
  )
}

export function WorkflowNav({ user }: WorkflowNavProps) {
  const location = useLocation()
  const navigate = useNavigate()
  const screens = useBreakpoint()

  // Filter modules based on user's feature tier access
  const visibleModules = WORKFLOW_MODULES.filter(module =>
    featureTierService.hasFeatureAccess(user, module.tier)
  )

  // Determine how many modules to show based on breakpoint
  // Keep order: Data, Cost, Price, Negotiate, Incentivize, Publish, Insights
  const getMaxVisible = (): number => {
    if (screens.xl) return visibleModules.length  // Show all at 1200px+
    if (screens.lg) return 5  // 992-1199px
    if (screens.md) return 4  // 768-991px
    return 3  // sm and xs (<768px)
  }

  const maxVisible = getMaxVisible()
  const needsMore = visibleModules.length > maxVisible

  // Split into visible and overflow (maintain order)
  const displayedModules = needsMore
    ? visibleModules.slice(0, maxVisible)
    : visibleModules
  const overflowModules = needsMore
    ? visibleModules.slice(maxVisible)
    : []

  // Show labels on sm+ (576px+)
  const showLabels = screens.sm !== false

  // Track active module
  const activeModule = visibleModules.find(m => location.pathname.startsWith(m.path))

  // Build "More" dropdown items
  const moreMenuItems = overflowModules.map(module => ({
    key: module.key,
    label: module.label,
    icon: React.createElement(module.icon),
    onClick: () => navigate(module.path)
  }))

  // Check if active module is in overflow (for "More" button highlight)
  const activeInOverflow = overflowModules.some(m => m.key === activeModule?.key)

  return (
    <nav
      style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
      aria-label="Module navigation"
    >
      {/* Visible modules */}
      {displayedModules.map(module => (
        <ModuleButton
          key={module.key}
          module={module}
          isActive={activeModule?.key === module.key}
          showLabel={showLabels}
          onClick={() => navigate(module.path)}
        />
      ))}

      {/* "More" dropdown when overflow exists */}
      {needsMore && (
        <Dropdown
          menu={{ items: moreMenuItems }}
          trigger={['click']}
          placement="bottomRight"
        >
          <button
            className={`header-btn${activeInOverflow ? ' active' : ''}`}
            aria-label="More modules"
            aria-haspopup="menu"
          >
            <EllipsisOutlined />
            {showLabels && <span>More</span>}
          </button>
        </Dropdown>
      )}
    </nav>
  )
}

export default WorkflowNav
